import React from "react";
import {
  Users2,
  Link2,
  ShieldCheck,
  Building2,
  Bot,
  Mail,
  Scale,
  Package,
  Share2,
  Car,
  Map,
  MapPin,
  Layers,
  Puzzle,
  PenLine,
  ChevronRight,
} from "lucide-react";

// V1.25 · Panel de accesos rápidos dentro de Configuración.
// Cada tarjeta navega a una pestaña existente (mismas keys que ya utiliza
// `Dashboard.jsx`) o hace scroll a una sección local de la propia
// Configuración. Ninguna funcionalidad se modifica — es puramente un hub
// de navegación visual que reduce el scroll y agrupa por categoría.

const CATEGORIES = [
  {
    key: "identidad",
    label: "Identidad y datos",
    cards: [
      { key: "autoescuela",   label: "Datos de la Autoescuela", icon: Building2, scrollTo: "settings-autoescuela" },
      { key: "shared_session", label: "Sesiones compartidas",   icon: Share2,    scrollTo: "settings-shared" },
    ],
  },
  {
    key: "seguridad",
    label: "Seguridad",
    cards: [
      { key: "seguridad", label: "Panel de seguridad completo", icon: ShieldCheck, nav: "seguridad",
        hint: "Usuarios conectados · Sesiones · IP · 2FA · Auditoría · Contraseñas" },
    ],
  },
  {
    key: "comunicacion",
    label: "Comunicación",
    cards: [
      { key: "kai",       label: "Agentes IA (Kai)",         icon: Bot,   scrollTo: "settings-kai" },
      { key: "templates", label: "Plantillas de mensajes",   icon: Mail,  scrollTo: "settings-templates" },
    ],
  },
  {
    key: "legal",
    label: "Legal y facturación",
    cards: [
      { key: "legal",   label: "Cláusulas legales",   icon: Scale,   scrollTo: "settings-legal" },
      { key: "signature_method", label: "Firma Digital · Método", icon: PenLine, scrollTo: "settings-signature-method",
        hint: "Directa, Email, SMS o Email/SMS — aplica a contratos y DGT" },
      { key: "rc",      label: "Centros de Reconocimiento DGT", icon: Building2, scrollTo: "settings-recognition-centers",
        hint: "Alta y edición de centros médicos con Nº DGT (auto-relleno)" },
      { key: "packs",   label: "Packs y facturación", icon: Package, scrollTo: "settings-packs" },
      { key: "accounting_sections", label: "Contabilidad · Secciones", icon: Layers, scrollTo: "settings-accounting-sections",
        hint: "Franquicias, centros o líneas de negocio (multi-sección)" },
      { key: "accounting_accounts", label: "Contabilidad · Cuentas y Tipos", icon: Layers, scrollTo: "settings-accounting-accounts",
        hint: "Plan contable global · Añade cuentas y tipos personalizados" },
    ],
  },
  {
    key: "operacion",
    label: "Operación",
    cards: [
      { key: "enlaces",   label: "Enlaces institucionales", icon: Link2,  nav: "enlaces" },
      { key: "referidos", label: "Referidos globales",      icon: Users2, nav: "referidos",
        hint: "Gestión masiva y estadísticas del programa de referidos" },
    ],
  },
  {
    key: "recursos",
    label: "Recursos (accesos rápidos)",
    cards: [
      { key: "vehiculos",   label: "Vehículos",   icon: Car,    nav: "vehiculos" },
      { key: "circuitos",   label: "Circuitos",   icon: Map,    nav: "circuitos" },
      { key: "ubicaciones", label: "Ubicaciones", icon: MapPin, nav: "ubicaciones" },
    ],
  },
  {
    key: "arquitectura",
    label: "Arquitectura corporativa",
    cards: [
      { key: "plugins_ecosystem", label: "Ecosistema de Plugins (SCME)", icon: Puzzle, scrollTo: "settings-plugins-ecosystem",
        hint: "Catálogo solo-lectura de módulos reutilizables · SCME v0.2" },
    ],
  },
];

function ConfigCard({ card, onClick }) {
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={() => onClick(card)}
      data-testid={`config-card-${card.key}`}
      className="group flex items-start gap-3 w-full text-left rounded-xl border border-neutral-200 bg-white hover:border-orange-300 hover:shadow-md transition-all p-4"
    >
      <div className="rounded-lg bg-orange-100 text-orange-700 p-2.5 group-hover:bg-orange-200 transition-colors">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-neutral-900 truncate">{card.label}</div>
        {card.hint && (
          <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{card.hint}</div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-400 group-hover:text-orange-500 shrink-0 mt-1" />
    </button>
  );
}

export default function ConfigDashboardHub({ onNavigateToTab }) {
  const handleCardClick = (card) => {
    if (card.nav && typeof onNavigateToTab === "function") {
      onNavigateToTab(card.nav);
      return;
    }
    if (card.scrollTo) {
      // V1.42.9 · Notifica al CollapsibleSection destino para que se abra
      // automáticamente antes de hacer scroll (evita landings ciegos).
      try {
        window.dispatchEvent(new CustomEvent("settings:open", { detail: card.scrollTo }));
      } catch { /* noop */ }
      const el = document.getElementById(card.scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // subtle highlight to guide the eye
        el.classList.add("ring-2", "ring-orange-300");
        setTimeout(() => el.classList.remove("ring-2", "ring-orange-300"), 1600);
      }
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 md:p-6" data-testid="config-hub">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-bold text-base">Configuración — accesos rápidos</h2>
        <span className="text-[10px] font-mono rounded bg-orange-100 text-orange-800 px-1.5 py-0.5">V1.25</span>
      </div>
      <p className="text-xs text-neutral-500 mb-5 max-w-2xl">
        Navega directamente a la sección que necesites sin bajar por la lista. Las tarjetas de "acceso rápido" abren la pestaña completa; las de "identidad" o "comunicación" te llevan al bloque correspondiente más abajo.
      </p>
      <div className="space-y-5">
        {CATEGORIES.map((cat) => (
          <div key={cat.key}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              {cat.label}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {cat.cards.map((card) => (
                <ConfigCard key={card.key} card={card} onClick={handleCardClick} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
